import api from "../config/api";
import { getErrorMessage } from "../config/errorHelper";

const saveToken    = (token) => localStorage.setItem("token", token);
const saveMfaToken = (token) => localStorage.setItem("mfa_token", token);
const clearToken   = ()      => { localStorage.removeItem("token"); localStorage.removeItem("mfa_token"); };
const getToken     = ()      => localStorage.getItem("token");
const getMfaToken  = ()      => localStorage.getItem("mfa_token");
const getAuthHeader = ()     => ({ headers: { Authorization: `Bearer ${getToken()}` } });

// ─── AUTH ────────────────────────────────────────────────────────
export const registerUser = async (userData) => {
  try { return (await api.post("/auth/register", userData)).data; }
  catch (error) { throw new Error(getErrorMessage(error)); }
};

// ÉTAPE 1 : email + mot de passe
// Retourne { requiresMfa: true, maskedEmail } si MFA requis
export const loginUser = async (credentials) => {
  try {
    const response = await api.post("/auth/login", credentials, { timeout: 30000 });
    const { code, message, data } = response.data;

    if (code === "ERROR") throw new Error(message);

    if (code === "MFA_REQUIRED") {
      saveMfaToken(data.mfaToken);
      return { requiresMfa: true, mfaToken: data.mfaToken, maskedEmail: data.maskedEmail };
    }

    saveToken(data.token);
    return { requiresMfa: false };
  } catch (error) {
    if (!error.response) throw error;
    throw new Error(getErrorMessage(error));
  }
};

// ÉTAPE 2 : vérifier le code MFA
export const verifyMfaCode = async (code, rememberMe = false) => {
  try {
    const mfaToken = getMfaToken();
    if (!mfaToken) throw new Error("Session MFA expirée, veuillez vous reconnecter");

    const response = await api.post(
      "/auth/verify-mfa",
      { code, rememberMe },
      { headers: { Authorization: `Bearer ${mfaToken}` } }
    );

    const { code: status, message, data } = response.data;
    if (status === "ERROR") throw new Error(message);

    saveToken(data.token);
    localStorage.removeItem("mfa_token");
    return data;
  } catch (error) {
    if (!error.response) throw error;
    throw new Error(getErrorMessage(error));
  }
};

// Renvoyer un nouveau code MFA
export const resendMfaCode = async () => {
  try {
    const mfaToken = getMfaToken();
    if (!mfaToken) throw new Error("Session MFA expirée");

    const response = await api.post(
      "/auth/resend-mfa",
      {},
      { headers: { Authorization: `Bearer ${mfaToken}` }, timeout: 30000 }
    );
    const { code, message } = response.data;
    if (code === "ERROR") throw new Error(message);
    return message;
  } catch (error) {
    if (!error.response) throw error;
    throw new Error(getErrorMessage(error));
  }
};

// ─── SESSION ────────────────────────────────────────────────────
export const logoutUser = async () => {
  try { await api.post("/users/logout", {}, getAuthHeader()); }
  finally { clearToken(); }
};

export const getMe = async () => {
  try { return (await api.get("/users/me", getAuthHeader())).data; }
  catch (error) { throw new Error(getErrorMessage(error)); }
};

/**
 * Récupère les permissions de l'utilisateur admin connecté.
 * GET /api/me → { data: { permissions: [...] } }
 * Utilisé par AuthContext pour construire can().
 * Ne lève jamais d'erreur bloquante : retourne un Set vide en cas d'échec
 * (comportement identique au portail client).
 */
export const fetchMyPermissions = async () => {
  try {
    const response = await api.get("/me", getAuthHeader());
    return new Set(response.data?.data?.permissions ?? []);
  } catch (error) {
    console.error("Impossible de charger les permissions :", error);
    return new Set();
  }
};

/**
 * Active ou désactive le code de vérification par email à la connexion
 * pour le compte administrateur connecté.
 * @param {boolean} enabled
 */
export const toggleMfa = async (enabled) => {
  try {
    const response = await api.put("/users/mfa-toggle", { enabled }, getAuthHeader());
    if (response.data?.code === "ERROR") throw new Error(response.data.message);
    return response.data;
  } catch (error) {
    if (!error.response) throw error;
    throw new Error(getErrorMessage(error));
  }
};

export const updateProfile     = async (d)   => { try { return (await api.put("/users/profile", d, getAuthHeader())).data; }                             catch(e){ throw new Error(getErrorMessage(e)); } };
export const changePassword    = async (o,n) => { try { return (await api.put("/users/change-password", {oldPassword:o,newPassword:n}, getAuthHeader())).data; } catch(e){ throw new Error(getErrorMessage(e)); } };
export const refreshToken      = async ()    => { try { const r = await api.post("/users/refresh-token",{},getAuthHeader()); saveToken(r.data.data); return r.data; } catch(e){ throw new Error(getErrorMessage(e)); } };
export const forgotPassword    = async (e)   => { try { return (await api.post("/users/forgot-password",{email:e})).data; }                              catch(err){ throw new Error(getErrorMessage(err)); } };
export const resetPassword     = async (t,p) => { try { return (await api.post("/users/reset-password",{token:t,newPassword:p})).data; }                catch(e){ throw new Error(getErrorMessage(e)); } };
export const getAllUsers        = async ()    => { try { return (await api.get("/users/all", getAuthHeader())).data; }                                    catch(e){ throw new Error(getErrorMessage(e)); } };
export const getUserById       = async (id)  => { try { return (await api.get(`/users/${id}`, getAuthHeader())).data; }                                  catch(e){ throw new Error(getErrorMessage(e)); } };
export const createUser        = async (d)   => { try { const r = await api.post("/users/admin/create",d,getAuthHeader()); if(r.data?.code==="ERROR") throw new Error(r.data.message); return r.data; } catch(e){ if(!(e.response||e.request)) throw e; throw new Error(getErrorMessage(e)); } };
export const updateUser        = async (id,d)=> { try { const r = await api.put(`/users/admin/update/${id}`,d,getAuthHeader()); if(r.data?.code==="ERROR") throw new Error(r.data.message); return r.data; } catch(e){ if(!(e.response||e.request)) throw e; throw new Error(getErrorMessage(e)); } };
export const deleteUser        = async (id)  => { try { return (await api.delete(`/users/admin/delete/${id}`, getAuthHeader())).data; }                  catch(e){ throw new Error(getErrorMessage(e)); } };
export const getAllRoles        = async ()    => { try { return (await api.get("/users/allRoles", getAuthHeader())).data; }                               catch(e){ throw new Error(getErrorMessage(e)); } };
export const getUsers          = async (cId) => { try { return (await api.get(`/users/admin/company/${cId}`)).data; }                                    catch(e){ throw new Error(getErrorMessage(e)); } };
export const toggleActive      = async (id,a)=> { try { return (await api.put(`/users/admin/toggle-active/${id}`,{active:a})).data; }                    catch(e){ throw new Error(getErrorMessage(e)); } };
export const resetUserPassword = async (id,p)=> { try { return (await api.put(`/users/admin/reset-password/${id}`,{newPassword:p})).data; }              catch(e){ throw new Error(getErrorMessage(e)); } };
export const getRoles          = async (cId) => { try { return (await api.get(`/roles/company/${cId}`)).data; }                                          catch(e){ throw new Error(getErrorMessage(e)); } };
export const getAllPermissions  = async ()   => { try { return (await api.get("/roles/permissions")).data; }                                              catch(e){ throw new Error(getErrorMessage(e)); } };
export const createRole        = async (d)   => { try { const r = await api.post("/roles/create",d); if(r.data?.code==="ERROR") throw new Error(r.data.message); return r.data; } catch(e){ if(!(e.response||e.request)) throw e; throw new Error(getErrorMessage(e)); } };
export const updateRolePermissions = async (id,p) => { try { const r = await api.put(`/roles/${id}/permissions`,{permissionNames:p}); if(r.data?.code==="ERROR") throw new Error(r.data.message); return r.data; } catch(e){ throw new Error(getErrorMessage(e)); } };
export const deleteRole        = async (id)  => { try { const r = await api.delete(`/roles/${id}`); if(r.data?.code==="ERROR") throw new Error(r.data.message); return r.data; } catch(e){ throw new Error(getErrorMessage(e)); } };